import distance from '@turf/distance'
import { lengthToDegrees } from '@turf/helpers'
import round from 'lodash/round'
import { visibleRegion } from '@/services/map-service'
import Geographic from '@/lib/geographic'

/**
 * Given a list of existing coordinates, determine the index position of a new
 * coordinate.  The new coordinate index is the index where the coordinate would
 * best fit, given an ordered list of coordinates for a line.
 * @param {Array<Array<number>>} coordinates - List of x, y tuples.
 * @param {Array<number>} newCoordinate - x, y tuple.
 * @returns {number} index where the new coordinate belongs in the coordinate
 * list.
 */
export const getIndexOfNewCoordinate = (coordinates, newCoordinate) => {
  let candidateMaxAngle = 0
  let candidateCoordinateIndex = null

  // Iterate over coordinate pairs (line segments).
  for (let i = 0; i < coordinates.length - 1; i++) {
    const coordinate1 = coordinates[i]
    const coordinate2 = coordinates[i + 1]
    if (Array.isArray(coordinate1) && Array.isArray(coordinate2)) {
      const vToC1 = distance(newCoordinate, coordinate1)
      const vToC1Squared = vToC1 * vToC1
      const vToC2 = distance(newCoordinate, coordinate2)
      const vToC2Squared = vToC2 * vToC2
      const c1ToC2 = distance(coordinate1, coordinate2)
      const c1ToC2Squared = c1ToC2 * c1ToC2

      // Compute angle of vertex from law of cosines.
      // https://en.wikipedia.org/wiki/Law_of_cosines
      const vertexAngle = Math.acos(
        (vToC1Squared + vToC2Squared - c1ToC2Squared) / (2 * vToC1 * vToC2)
      )

      // The best match is the vertex with the largest angle compared to the other
      // two line points.  A point lying perfectly on the line should have an
      // angle of 180 degrees relative to the line end points.  A point lying
      // outside of the line would approach zero degrees.
      if (vertexAngle > candidateMaxAngle) {
        candidateMaxAngle = vertexAngle
        candidateCoordinateIndex = i + 1
      }
    }
  }

  return candidateCoordinateIndex
}

/**
 * Given the list of coordinates in the line feature, and the current
 * coordinate being hovered by the user, return the closest coordinate to the
 * hovered coordinate on the line.
 * @param {Array<Array<number>>} lineCoordinates - List of x, y tuples.
 * @param {Array<number>} hoverCoordinate - x, y tuple.
 * @returns {Array<number>} x, y tuple of the closest point on the line to the passed
 * hoverCoordinate
 */
export const getNearestPointOnLine = (lineCoordinates, hoverCoordinate) => {
  const closestIndex = getIndexOfNewCoordinate(lineCoordinates, hoverCoordinate)

  // a and b are the two closest points on the line
  const a = lineCoordinates[closestIndex - 1]
  const b = lineCoordinates[closestIndex]

  // given points a and b, calculate the slope and intercept for the line between them
  const slope1 = (b[1] - a[1]) / (b[0] - a[0])
  const intercept1 = -1 * (slope1 * a[0] - a[1])

  // given points a, b, and p, calculate the slope and intercept of the line drawn perpendicularly
  // from point p to the line between points a and b.
  const p = hoverCoordinate
  const slope2 = (-1 * (b[0] - a[0])) / (b[1] - a[1])
  const intercept2 = -1 * (slope2 * p[0] - p[1])

  // set the the slope intercept form for the two lines equal and solve for x, then use x to
  // solve for y to find the point where the two lines intersect
  const x = (intercept2 - intercept1) / (slope1 - slope2)
  const y = slope1 * x + intercept1

  return [x, y, 0]
}

/**
 * Find the index of the provided point in the coordinates 2d array.
 * @param {Array} coordinates - 2d array of lat,lon
 * @param {Array} point - lat,lon array
 * @returns {Number} - Index of point in coordinates
 */
export const getCoordIndexByPoint = (coordinates, point) => {
  // round and stringify point array for comparison
  const latPointClean = round(point[0], 5)
  const lonPointClean = round(point[1], 5)

  // find first matching coord
  const matchingCoord = coordinates.find((coord) => {
    const latCoordClean = round(coord[0], 5)
    const lonCoordClean = round(coord[1], 5)
    const latMatch = latPointClean === latCoordClean
    const lonMatch = lonPointClean === lonCoordClean
    return latMatch && lonMatch
  })

  // return index of matched coord
  return coordinates.indexOf(matchingCoord)
}

/**
 * Returns tolerance in meters for a given tolerance value in pixels, taking into
 * account current visible region and screen resolution.
 * @param {number} tolerancePixels - Desired tolerance value in pixels
 * @returns {number} Tolerance value in meters
 */
export const getToleranceMeters = (tolerancePixels) => {
  const region = visibleRegion().geometry.coordinates[0]
  const x0 = region[0][0]
  const x1 = region[2][0]
  const y0 = region[0][1]
  const y1 = region[2][1]
  const { x: upper_left_x, y: upper_left_y } = new Geographic(
    x0,
    y0
  ).toMercator()
  const { x: lower_right_x, y: lower_right_y } = new Geographic(
    x1,
    y1
  ).toMercator()
  const widthMeters = lower_right_x - upper_left_x
  const heightMeters = upper_left_y - lower_right_y
  const horzRes = widthMeters / window.innerWidth
  const vertRes = heightMeters / window.innerHeight
  const resolution = Math.min(horzRes, vertRes)
  return resolution * tolerancePixels
}

/**
 * Returns tolerance in degrees for a given tolerance value in pixels, taking into
 * account current visible region and screen resolution. Used for simplifying
 * freehand drawn lines and shapes.
 * @param {number} tolerancePixels - Desired tolerance value in pixels
 * @returns {number} Tolerance value in degrees (may be passed to simplify function)
 */
export const getToleranceDegrees = (tolerancePixels) => {
  const toleranceMeters = getToleranceMeters(tolerancePixels)
  return lengthToDegrees(toleranceMeters, 'meters')
}

/**
 * Finds the nearest vertex to a clicked point within a specified threshold distance.
 *
 * @param {Object} line - GeoJSON line object containing geometry with coordinates
 * @param {Array<number>} point - The clicked point coordinates [longitude, latitude]
 * @param {Object} [options] - Configuration options
 * @param {number} [options.threshold=8] - Maximum distance threshold in meters
 * @returns {Object} Result object containing vertex information
 * @returns {Array<number>|null} returns.vertexCoordinates - Coordinates of nearest vertex, or null if none found
 * @returns {number} returns.index - Index of the nearest vertex in coordinates array, or -1 if none found
 * @returns {number} returns.distance - Distance in meters to the nearest vertex, or Infinity if none found
 *
 * @example
 * const line = {
 *   geometry: {
 *     coordinates: [[0, 0], [1, 1], [2, 2]]
 *   }
 * };
 * const clickPoint = [0.1, 0.1];
 * const result = findVertexNearPoint(line, clickPoint, { threshold: 10 });
 * // Returns: { vertexCoordinates: [0, 0], index: 0, distance: 15.7 }
 */
export const findVertexNearPoint = (
  line,
  point,
  options = { threshold: 10 }
) => {
  const { coordinates } = line.geometry

  let nearestVertex = null
  let nearestIndex = -1
  let minDistance = Infinity

  // run through the coordinates until we find a vertex that is within the threshold
  // (using a traditional for loop here for any speed improvement on large lines)
  // Initially we tried breaking out of the loop when you find a match, but this
  // is an issue with out-and-back lines where vertices might be physically close
  // to each other, but not adjacent in the coords array
  for (let i = 0; i < coordinates.length; i++) {
    const distanceFromPoint = distance(point, coordinates[i], {
      units: 'meters',
    })
    if (
      distanceFromPoint <= options.threshold &&
      distanceFromPoint < minDistance
    ) {
      nearestVertex = coordinates[i]
      nearestIndex = i
      minDistance = distanceFromPoint
    }
  }

  return {
    vertexCoordinates: nearestVertex,
    index: nearestIndex,
    distance: minDistance,
  }
}
